import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import FileProcessingService from '../services/FileProcessingService';

const OperationsContext = createContext();

export const useOperations = () => {
  const context = useContext(OperationsContext);
  if (!context) {
    throw new Error('useOperations must be used within an OperationsProvider');
  }
  return context;
};

export const OperationsProvider = ({ children }) => {
  const [processingStatus, setProcessingStatus] = useState({
    queue: [],
    totalFiles: 0,
    processing: [],
    completed: 0,
    failed: 0,
    pending: 0
  });
  
  const [isOperationsExpanded, setIsOperationsExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Memoized derived state to prevent unnecessary re-renders
  const operationsState = useMemo(() => {
    const { queue } = processingStatus;
    
    // Group files by status
    const filesByStatus = queue.reduce((acc, file) => {
      if (!acc[file.status]) acc[file.status] = [];
      acc[file.status].push(file);
      return acc;
    }, {});

    // Sort files: failed first, then processing, then pending, then uploading, then completed
    const statusOrder = {
      'failed': 1,
      'processing': 2,
      'pending': 3,
      'uploading': 4,
      'completed': 5,
      'cancelled': 6
    };

    const sortedFiles = queue.sort((a, b) => {
      const aOrder = statusOrder[a.status] || 999;
      const bOrder = statusOrder[b.status] || 999;
      
      if (aOrder !== bOrder) return aOrder - bOrder;
      
      // Within same status, sort by start time (newest first)
      return (b.startTime || 0) - (a.startTime || 0);
    });

    // Calculate progress for segmented progress bar
    const totalFiles = queue.length;
    const completedFiles = filesByStatus.completed?.length || 0;
    const failedFiles = filesByStatus.failed?.length || 0;
    const processingFiles = filesByStatus.processing?.length || 0;
    const pendingFiles = filesByStatus.pending?.length || 0;
    const uploadingFiles = filesByStatus.uploading?.length || 0;
    
    const progress = totalFiles > 0 ? (completedFiles / totalFiles) * 100 : 0;
    
    // Group files by project/collection for indicators
    const filesByProject = queue.reduce((acc, file) => {
      const key = file.collectionId || file.projectId || 'unknown';
      if (!acc[key]) acc[key] = [];
      acc[key].push(file);
      return acc;
    }, {});

    const hasActiveOperations = totalFiles > 0 && (processingFiles > 0 || pendingFiles > 0 || uploadingFiles > 0);
    const hasFailedOperations = failedFiles > 0;

    return {
      totalFiles,
      completedFiles,
      failedFiles,
      processingFiles,
      pendingFiles,
      uploadingFiles,
      progress,
      filesByStatus,
      filesByProject,
      sortedFiles,
      hasActiveOperations,
      hasFailedOperations,
      isEmpty: totalFiles === 0
    };
  }, [processingStatus]);

  // Debounced status update to reduce re-renders
  const updateProcessingStatus = useCallback((status) => {
    setProcessingStatus(status);
    setLastUpdate(Date.now());
    
    // Auto-expand operations panel when new files are added
    if (status.totalFiles > processingStatus.totalFiles && !isOperationsExpanded) {
      setIsOperationsExpanded(true);
    }
  }, [processingStatus.totalFiles, isOperationsExpanded]);

  useEffect(() => {
    // Subscribe to processing updates with debouncing
    let updateTimeout;
    
    const unsubscribe = FileProcessingService.addListener((status) => {
      // Clear existing timeout
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      
      // Debounce updates to prevent rapid re-renders
      updateTimeout = setTimeout(() => {
        updateProcessingStatus(status);
      }, 50); // 50ms debounce
    });

    // Initial status
    updateProcessingStatus(FileProcessingService.getProcessingStatus());

    return () => {
      unsubscribe();
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
    };
  }, [updateProcessingStatus]);

  // File action handlers
  const handleCancelFile = useCallback((tempId) => {
    FileProcessingService.cancelFile(tempId);
  }, []);

  const handleRetryFile = useCallback((tempId) => {
    FileProcessingService.retryFile(tempId);
  }, []);

  const handleRemoveFile = useCallback((tempId) => {
    FileProcessingService.removeFile(tempId);
  }, []);

  const handleClearCompleted = useCallback(() => {
    return FileProcessingService.clearCompletedFiles();
  }, []);

  // Check if project/collection has active operations
  const hasActiveOperationsFor = useCallback((projectId, collectionId = null) => {
    const key = collectionId || projectId;
    const files = operationsState.filesByProject[key] || [];
    return files.some(file => ['uploading', 'pending', 'processing'].includes(file.status));
  }, [operationsState.filesByProject]);

  // Check if project/collection has failed operations
  const hasFailedOperationsFor = useCallback((projectId, collectionId = null) => {
    const key = collectionId || projectId;
    const files = operationsState.filesByProject[key] || [];
    return files.some(file => file.status === 'failed');
  }, [operationsState.filesByProject]);

  const toggleOperationsExpanded = useCallback(() => {
    setIsOperationsExpanded(prev => !prev);
  }, []);

  const contextValue = {
    // State
    ...operationsState,
    isOperationsExpanded,
    lastUpdate,
    
    // Raw processing status for compatibility
    processingStatus,
    
    // Actions
    handleCancelFile,
    handleRetryFile,
    handleRemoveFile,
    handleClearCompleted,
    toggleOperationsExpanded,
    
    // Utilities
    hasActiveOperationsFor,
    hasFailedOperationsFor,
  };

  return (
    <OperationsContext.Provider value={contextValue}>
      {children}
    </OperationsContext.Provider>
  );
};